import type { Issue, Person } from '../game/types';
import { PRIORITY_GLYPH, TYPE_GLYPH, TYPE_LABEL, shortDate } from '../game/format';

interface Props {
  issue: Issue;
  assignee: Person | undefined;
  flagged: boolean;
  /** Sprint this board is showing, so a mismatched sprint can be surfaced. */
  currentSprint: string;
  isDoneColumn: boolean;
  onToggleFlag: () => void;
  onOpenDetail: () => void;
}

/**
 * One ticket on the board.
 *
 * The card body is a single button that toggles the flag -- on a phone that is
 * one tap to accuse, which is the core loop. The info button is a sibling, not
 * a child, because nesting interactive elements breaks both keyboard order and
 * screen readers.
 *
 * Everything a player needs in order to spot a planted defect is rendered here
 * on the face of the card. The detail sheet only ever corroborates.
 */
export function IssueCard({
  issue,
  assignee,
  flagged,
  currentSprint,
  isDoneColumn,
  onToggleFlag,
  onOpenDetail,
}: Props) {
  const estimate = renderEstimate(issue);
  const sprintMismatch = issue.sprint !== undefined && issue.sprint !== currentSprint;

  return (
    <div className={`card${flagged ? ' is-flagged' : ''}`}>
      {flagged && (
        <span className="flag-marker" aria-hidden="true">
          ⚑
        </span>
      )}

      <button
        type="button"
        className="card__main"
        aria-pressed={flagged}
        aria-label={`${TYPE_LABEL[issue.type] ?? issue.type} ${issue.key}: ${issue.summary}. ${
          flagged ? 'Flagged as a problem.' : 'Not flagged.'
        } Activate to toggle.`}
        onClick={onToggleFlag}
      >
        <div className="card__summary">{issue.summary}</div>

        {(issue.labels?.length ||
          issue.blockedBy?.length ||
          issue.parentKey ||
          issue.linkedPr ||
          sprintMismatch ||
          issue.dueDate) && (
          <div className="card__labels">
            {issue.blockedBy?.map((key) => (
              <span key={key} className="label label--blocked">
                blocked by {key}
              </span>
            ))}
            {issue.parentKey && (
              <span className="label label--parent">↳ {issue.parentKey}</span>
            )}
            {sprintMismatch && (
              <span className="label label--sprint">{issue.sprint}</span>
            )}
            {issue.linkedPr && (
              <span className="label label--pr">MR {issue.linkedPr}</span>
            )}
            {issue.dueDate && (
              <span className="label">
                {issue.startDate ? `${shortDate(issue.startDate)} → ` : 'due '}
                {shortDate(issue.dueDate)}
              </span>
            )}
            {issue.labels?.map((label) => (
              <span key={label} className="label">
                {label}
              </span>
            ))}
          </div>
        )}

        <div className="card__footer">
          <span
            className={`type-icon type-icon--${issue.type}`}
            title={TYPE_LABEL[issue.type]}
            aria-hidden="true"
          >
            {TYPE_GLYPH[issue.type]}
          </span>

          <span className={`card__key${isDoneColumn ? ' card__key--done' : ''}`}>
            {issue.key}
          </span>

          <span
            className={`priority priority--${issue.priority}`}
            title={`${issue.priority} priority`}
            aria-hidden="true"
          >
            {PRIORITY_GLYPH[issue.priority]}
          </span>

          <span className="card__spacer" />

          {issue.idleDays !== undefined && (
            <span
              className={`idle${issue.idleDays >= 30 ? ' idle--stale' : ''}`}
              title={`${issue.idleDays} days since last update`}
            >
              {issue.idleDays}d
            </span>
          )}

          <span className={estimate.className} title={estimate.title}>
            {estimate.text}
          </span>

          {assignee ? (
            <span
              className={`avatar${assignee.inactive ? ' avatar--inactive' : ''}`}
              style={{ background: assignee.color }}
              title={`${assignee.name}${assignee.inactive ? ' (deactivated account)' : ''}`}
            >
              {assignee.initials}
            </span>
          ) : (
            <span className="avatar avatar--none" title="Unassigned">
              ?
            </span>
          )}
        </div>
      </button>

      <button
        type="button"
        className="card__info"
        aria-label={`Open details for ${issue.key}`}
        onClick={onOpenDetail}
      >
        ⓘ
      </button>
    </div>
  );
}

/**
 * The estimate badge carries three distinct meanings, so it gets three looks:
 * a normal point value, a non-standard unit (hours), or nothing estimated.
 */
function renderEstimate(issue: Issue): {
  text: string;
  className: string;
  title: string;
} {
  if (issue.points !== undefined) {
    return {
      text: String(issue.points),
      className: 'points',
      title: `${issue.points} story points`,
    };
  }
  if (issue.estimateHours !== undefined) {
    return {
      text: `${issue.estimateHours}h`,
      className: 'points points--hours',
      title: `${issue.estimateHours} hours — not the board's estimation unit`,
    };
  }
  return { text: '–', className: 'points points--none', title: 'No estimate' };
}
